import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today as TodayIcon
} from '@mui/icons-material';
import ContainerNav from './ContainerNav';
import { api } from '../config/api';

// --- Types ---

interface ContainerDetail {
  container_no: string;
  container_id: number | null;
  container_status: string | null;
  arrival_date: string | null;
  last_free_day: string | null;
  free_days_demurrage: number | null;
  days_remaining: number | null;
}

interface POGroup {
  po_number: string;
  supplier_code: string;
  expected_delivery_by: string | null;
  containers: ContainerDetail[];
  earliest_arrival: string | null;
  latest_last_free_day: string | null;
  container_count: number;
}

// --- Constants ---

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// --- Helpers ---

function parseLocalDate(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// --- Component ---

const ContainerExposureCalendar: React.FC = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [poGroups, setPOGroups] = useState<POGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hideReceived, setHideReceived] = useState(false);

  // Detail panel
  const [detailPO, setDetailPO] = useState<POGroup | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const monthStr = `${year}-${String(month).padStart(2, '0')}`;
      const res = await api.get(`/container-exposure?month=${monthStr}`);
      if (res.data.success) {
        setPOGroups(res.data.po_groups);
      } else {
        setError(res.data.error || 'Failed to load data');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Month navigation
  const prevMonth = () => {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else { setMonth(m => m - 1); }
  };
  const nextMonth = () => {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else { setMonth(m => m + 1); }
  };
  const goToday = () => {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth() + 1);
  };

  // Build calendar grid (Monday-first)
  const buildCalendarDays = (): (Date | null)[][] => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    // Convert Sunday=0 to Monday-first: Mon=0, Tue=1, ..., Sun=6
    const startDow = (firstDay.getDay() + 6) % 7;

    const weeks: (Date | null)[][] = [];
    let currentWeek: (Date | null)[] = [];

    for (let i = 0; i < startDow; i++) currentWeek.push(null);

    for (let d = 1; d <= lastDay.getDate(); d++) {
      currentWeek.push(new Date(year, month - 1, d));
      if (currentWeek.length === 7) {
        weeks.push(currentWeek);
        currentWeek = [];
      }
    }

    if (currentWeek.length > 0) {
      while (currentWeek.length < 7) currentWeek.push(null);
      weeks.push(currentWeek);
    }

    return weeks;
  };

  const filteredPOGroups = hideReceived
    ? poGroups.filter(g => !(g.containers.length > 0 && g.containers.every(c => c.container_status === 'received')))
    : poGroups;

  const weeks = buildCalendarDays();

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <ContainerNav title="Container Management" />

      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Demurrage Exposure Calendar
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Month Navigation */}
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={prevMonth}><ChevronLeft /></IconButton>
          <Typography variant="h6" sx={{ minWidth: 180, textAlign: 'center' }}>
            {MONTH_NAMES[month - 1]} {year}
          </Typography>
          <IconButton onClick={nextMonth}><ChevronRight /></IconButton>
          <Button size="small" variant="outlined" startIcon={<TodayIcon />} onClick={goToday}>
            Today
          </Button>
        </Box>
        <FormControlLabel
          control={<Checkbox checked={hideReceived} onChange={e => setHideReceived(e.target.checked)} size="small" />}
          label={<Typography variant="body2">Hide fully received</Typography>}
        />
      </Box>

      {/* Calendar Grid */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ overflow: 'hidden' }}>
          {/* Header row */}
          <Box
            display="grid"
            gridTemplateColumns="repeat(7, 1fr)"
            sx={{ bgcolor: 'grey.100', borderBottom: '1px solid', borderColor: 'divider' }}
          >
            {DAY_NAMES.map(day => (
              <Box key={day} sx={{ p: 1, textAlign: 'center' }}>
                <Typography variant="body2" fontWeight={600} color="text.secondary">{day}</Typography>
              </Box>
            ))}
          </Box>

          {/* Week rows — single grid: row 1 = date numbers, rows 2+ = PO bars */}
          {weeks.map((week, wi) => {
            // Find PO bars that overlap this week
            const weekBars: { g: POGroup; startCol: number; endCol: number; continuesLeft: boolean; continuesRight: boolean }[] = [];
            filteredPOGroups.forEach(g => {
              if (!g.earliest_arrival) return;
              const arr = parseLocalDate(g.earliest_arrival);
              const end = g.latest_last_free_day ? parseLocalDate(g.latest_last_free_day) : arr;

              const weekDays = week.map((d, i) => ({ d, i })).filter(x => x.d !== null) as { d: Date; i: number }[];
              if (weekDays.length === 0) return;

              const firstWeekDate = weekDays[0].d;
              const lastWeekDate = weekDays[weekDays.length - 1].d;
              if (arr > lastWeekDate || end < firstWeekDate) return;

              const continuesLeft = arr < firstWeekDate;
              const continuesRight = end > lastWeekDate;
              const barStart = continuesLeft ? firstWeekDate : arr;
              const barEnd = continuesRight ? lastWeekDate : end;

              const sc = weekDays.find(x => isSameDay(x.d, barStart))?.i ?? weekDays.find(x => x.d >= barStart)?.i;
              const ec = weekDays.find(x => isSameDay(x.d, barEnd))?.i ?? weekDays[weekDays.length - 1].i;
              if (sc == null || ec == null) return;
              weekBars.push({ g, startCol: sc + 1, endCol: ec + 2, continuesLeft, continuesRight });
            });

            // Sort: received POs to bottom
            weekBars.sort((a, b) => {
              const aReceived = a.g.containers.length > 0 && a.g.containers.every(c => c.container_status === 'received');
              const bReceived = b.g.containers.length > 0 && b.g.containers.every(c => c.container_status === 'received');
              if (aReceived === bReceived) return 0;
              return aReceived ? 1 : -1;
            });

            return (
              <Box
                key={wi}
                display="grid"
                gridTemplateColumns="repeat(7, 1fr)"
                sx={{
                  borderBottom: wi < weeks.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  gridTemplateRows: `auto${weekBars.length > 0 ? ` repeat(${weekBars.length}, 20px)` : ''}`,
                  rowGap: '2px',
                  pb: weekBars.length > 0 ? 0.5 : 0,
                }}
              >
                {/* Row 1: Date number cells */}
                {week.map((day, di) => {
                  const isToday = day ? isSameDay(day, today) : false;
                  return (
                    <Box
                      key={di}
                      sx={{
                        gridColumn: di + 1,
                        gridRow: 1,
                        p: 0.5,
                        borderRight: di < 6 ? '1px solid' : 'none',
                        borderColor: 'divider',
                        bgcolor: isToday ? 'rgba(25, 118, 210, 0.06)' : day ? 'white' : 'grey.50',
                        minHeight: weekBars.length === 0 ? 60 : 24,
                      }}
                    >
                      {day && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: isToday ? 700 : 400,
                            color: isToday ? 'primary.main' : 'text.secondary',
                          }}
                        >
                          {day.getDate()}
                        </Typography>
                      )}
                    </Box>
                  );
                })}

                {/* Rows 2+: PO bars spanning columns */}
                {weekBars.map((bar, bi) => (
                  <Tooltip
                    key={`${bar.g.po_number}-${wi}`}
                    arrow
                    title={
                      <Box sx={{ p: 0.5 }}>
                        <Typography variant="body2" fontWeight={600}>
                          PO {bar.g.po_number} ({bar.g.supplier_code})
                        </Typography>
                        <Typography variant="caption" display="block">
                          {bar.g.container_count} container{bar.g.container_count !== 1 ? 's' : ''}
                        </Typography>
                        {bar.g.earliest_arrival && (
                          <Typography variant="caption" display="block">ETA: {bar.g.earliest_arrival}</Typography>
                        )}
                        {bar.g.latest_last_free_day && (
                          <Typography variant="caption" display="block">Last Free Day: {bar.g.latest_last_free_day}</Typography>
                        )}
                      </Box>
                    }
                  >
                    <Box
                      onClick={() => setDetailPO(bar.g)}
                      sx={{
                        gridColumn: `${bar.startCol} / ${bar.endCol}`,
                        gridRow: bi + 2,
                        height: 18,
                        ml: bar.continuesLeft ? 0 : '2px',
                        mr: bar.continuesRight ? 0 : '2px',
                        borderRadius: bar.continuesLeft || bar.continuesRight
                          ? `${bar.continuesLeft ? 0 : 4}px ${bar.continuesRight ? 0 : 4}px ${bar.continuesRight ? 0 : 4}px ${bar.continuesLeft ? 0 : 4}px`
                          : 1,
                        bgcolor: bar.g.containers.length > 0 && bar.g.containers.every(c => c.container_status === 'received')
                          ? '#4caf50' : '#5c6bc0',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 0.5,
                        overflow: 'hidden',
                        '&:hover': {
                          opacity: 0.85,
                          boxShadow: 1
                        }
                      }}
                    >
                      {bar.continuesLeft && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem', lineHeight: 1, mr: 0.25 }}>&#9664;</Typography>
                      )}
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'white',
                          fontSize: '0.6rem',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          lineHeight: 1,
                          flex: 1,
                        }}
                      >
                        {(() => {
                          const received = bar.g.containers.filter(c => c.container_status === 'received').length;
                          const planned = bar.g.containers.filter(c => c.container_status !== 'received' && c.arrival_date).length;
                          return `${bar.g.po_number} \u00B7 ${bar.g.supplier_code} (${planned} planned | ${received} received | ${bar.g.container_count} total)`;
                        })()}
                      </Typography>
                      {bar.continuesRight && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.6rem', lineHeight: 1, ml: 0.25 }}>&#9654;</Typography>
                      )}
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            );
          })}
        </Paper>
      )}

      {/* Legend */}
      <Box display="flex" gap={3} sx={{ mt: 2, justifyContent: 'center' }}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 24, height: 12, borderRadius: 1, bgcolor: '#5c6bc0' }} />
          <Typography variant="caption" color="text.secondary">Free day period</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Box sx={{ width: 24, height: 12, borderRadius: 1, bgcolor: '#4caf50' }} />
          <Typography variant="caption" color="text.secondary">Fully received</Typography>
        </Box>
      </Box>

      {/* PO Detail Panel (shows when clicking a bar) */}
      <Dialog open={!!detailPO} onClose={() => setDetailPO(null)} maxWidth="md" fullWidth>
        {detailPO && (
          <>
            <DialogTitle>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6">PO {detailPO.po_number}</Typography>
                <Chip label={detailPO.supplier_code} size="small" />
              </Box>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {detailPO.container_count} container{detailPO.container_count !== 1 ? 's' : ''}
                {detailPO.expected_delivery_by && ` \u00B7 Expected: ${detailPO.expected_delivery_by}`}
                {detailPO.earliest_arrival && ` \u00B7 ETA: ${detailPO.earliest_arrival}`}
              </Typography>

              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Container</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Arrival</TableCell>
                    <TableCell>Free Days</TableCell>
                    <TableCell>Last Free Day</TableCell>
                    <TableCell>Days Left</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detailPO.containers.map(c => (
                    <TableRow key={c.container_no}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{c.container_no}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={c.container_status || 'unknown'}
                          size="small"
                          color={c.container_status === 'received' ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{c.arrival_date || '-'}</TableCell>
                      <TableCell>{c.free_days_demurrage ?? '-'}</TableCell>
                      <TableCell>{c.last_free_day || '-'}</TableCell>
                      <TableCell>
                        {c.days_remaining != null ? (
                          c.days_remaining >= 0 ? `${c.days_remaining}d` : `${Math.abs(c.days_remaining)}d overdue`
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailPO(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ContainerExposureCalendar;
